import { useMemo } from 'react';
import type {
  PlanningPageQuery,
  PlanningDataUpdatedSubscription,
  Exact,
  LocationUpdatedSubscription,
  ShipsUpdatedSubscription,
  AssortmentUpdatedSubscription,
  OrganizationUpdatedSubscription,
  ScenarioTimeline_ShipFragment,
  ScenarioTimeline_OrderLocationFragment,
  ScenarioTimeline_AssortmentFragment,
  ScenarioTimeline_OrganizationFragment,
  VoyageAttributeUpdatedSubscription,
  AllVoyageAttributesQuery,
  ShipmentUpdatedSubscription,
  RobTaskUpdateSubscription,
  VesonBunkerQuery,
} from '../../graphql';
import {
  PlanningPageDocument,
  usePlanningDataUpdatedSubscription,
  useLocationUpdatedSubscription,
  useShipsUpdatedSubscription,
  useOrganizationUpdatedSubscription,
  useAssortmentUpdatedSubscription,
  useVoyageAttributeUpdatedSubscription,
  AllVoyageAttributesDocument,
  useShipmentUpdatedSubscription,
  useRobTaskUpdateSubscription,
  VesonBunkerDocument,
} from '../../graphql';
import type { OnDataOptions, SubscriptionHookOptions } from '@apollo/client';
import { isDefined } from '../../lib/util';
import isEqual from '@wry/equality';
import { useAllLocations } from '../../lib/allLocations';
import { useSetAtom } from 'jotai';
import { allLocationsQueryResult } from '../../lib/allLocations';
import { toast } from 'react-toastify';
import { mapById } from '@seaber/common/lib/collections';
import { globalStore } from '../../state/zustand';

const planningPageQueryVariables = {
  showArchived: false,
};

type EntityGetter<
  Subscription extends
    | ShipsUpdatedSubscription
    | LocationUpdatedSubscription
    | AssortmentUpdatedSubscription
    | OrganizationUpdatedSubscription
    | VoyageAttributeUpdatedSubscription
    | ShipmentUpdatedSubscription,
  Entity extends
    | ScenarioTimeline_ShipFragment
    | ScenarioTimeline_OrderLocationFragment
    | ScenarioTimeline_AssortmentFragment
    | ScenarioTimeline_OrganizationFragment,
> = (data: Subscription | undefined) => {
  entity: Entity | null | undefined;
  event: string | null | undefined;
};

const isBrowser = typeof window !== 'undefined';

function createPlanningPageQuerySubscriptionHandler<
  Subscription extends
    | ShipsUpdatedSubscription
    | OrganizationUpdatedSubscription,
  Entity extends
    | ScenarioTimeline_ShipFragment
    | ScenarioTimeline_OrganizationFragment,
  RootField extends keyof Omit<PlanningPageQuery, '__typename'>,
>(entityGetter: EntityGetter<Subscription, Entity>, key: RootField) {
  return function (options: OnDataOptions<Subscription>): void {
    const { client, data } = options;
    const { entity, event } = entityGetter(data?.data);
    const dataInStore = client.readQuery<PlanningPageQuery>({
      query: PlanningPageDocument,
      variables: planningPageQueryVariables,
    });
    const rootField = dataInStore?.[key];
    const nodes = rootField?.nodes;
    if (!nodes) {
      return;
    }
    if (entity) {
      const id = entity.id;
      if (!nodes.find((o) => o && 'id' in o && o.id === id)) {
        client.writeQuery({
          query: PlanningPageDocument,
          variables: planningPageQueryVariables,
          data: {
            ...dataInStore,
            [key]: { ...rootField, nodes: [...nodes, entity] },
          },
        });
      }
    } else if (event) {
      const id = +event;
      if (nodes.find((o) => o && 'id' in o && o.id === id)) {
        client.writeQuery({
          query: PlanningPageDocument,
          variables: planningPageQueryVariables,
          data: {
            ...dataInStore,
            [key]: {
              ...rootField,
              nodes: nodes.filter((o) => o && (!('id' in o) || o.id !== id)),
            },
          },
        });
      }
    }
  };
}

export function usePlanningUpdates(
  organization_id: number | null | undefined,
): void {
  const setAllLocationsResult = useSetAtom(allLocationsQueryResult);

  const baseOptions: SubscriptionHookOptions<
    PlanningDataUpdatedSubscription,
    Exact<{ org: string }>
  > = useMemo(() => {
    return {
      variables: {
        org: String(organization_id),
      },
      skip: organization_id == null || !isBrowser,
      onData: function (
        options: OnDataOptions<PlanningDataUpdatedSubscription>,
      ): void {
        const { client, data } = options;
        const { order, event } = data?.data?.planningDataUpdated || {};

        try {
          const dataInStore = client.readQuery<PlanningPageQuery>({
            query: PlanningPageDocument,
            variables: planningPageQueryVariables,
          });
          const allOrders = dataInStore?.allOrders;
          const nodes = allOrders?.nodes;

          if (!nodes) {
            return;
          }
          if (!order && event) {
            const id = +event;
            if (nodes.find((o) => o?.id === id)) {
              client.writeQuery({
                query: PlanningPageDocument,
                variables: planningPageQueryVariables,
                data: {
                  ...dataInStore,
                  allOrders: {
                    ...allOrders,
                    nodes: nodes.filter((o) => o?.id !== id),
                  },
                },
              });
            }
            return;
          }
          if (!order) {
            return;
          }
          if (order.softDelete) {
            const id = order.id;
            const existingOrder = nodes.find((o) => o?.id === id);
            const updatedNodes = nodes.filter(
              (o) =>
                existingOrder
                  ? o?.id !== id // Remove exact match if order exists
                  : o?.id && o.id !== id, // Remove and handle null/undefined IDs if order doesn't exist
            );

            client.writeQuery({
              query: PlanningPageDocument,
              variables: planningPageQueryVariables,
              data: {
                ...dataInStore,
                allOrders: {
                  ...allOrders,
                  nodes: updatedNodes,
                },
              },
            });
            return;
          }

          let data = dataInStore;

          const orderId = order.id;
          if (!nodes.find((o) => o?.id === orderId)) {
            data = {
              ...data,
              allOrders: { ...allOrders, nodes: [...nodes, order] },
            };
          }

          const {
            scheduledOrdersByOrderId: {
              nodes: [so],
            },
            handledBySpotByOrderId: hbs,
            handledByCoaByOrderId: hbc,
          } = order;

          const allScheduledOrders = dataInStore?.allScheduledOrders;
          const currentNodes = allScheduledOrders?.nodes.filter(isDefined);

          if (currentNodes && allScheduledOrders) {
            const updated = getUpdatedNodes(order.id, so, currentNodes);

            if (updated) {
              data = {
                ...data,
                allScheduledOrders: {
                  ...allScheduledOrders,
                  nodes: updated,
                },
              };
            }
          }

          const allHandledBySpots = dataInStore?.allHandledBySpots;
          const hbsNodes = allHandledBySpots?.nodes.filter(isDefined);

          if (hbsNodes && allHandledBySpots) {
            const updated = getUpdatedNodes(order.id, hbs, hbsNodes);

            if (updated) {
              data = {
                ...data,
                allHandledBySpots: {
                  ...allHandledBySpots,
                  nodes: updated,
                },
              };
            }
          }

          const allHandledByCoas = dataInStore?.allHandledByCoas;
          const hbcNodes = allHandledByCoas?.nodes.filter(isDefined);
          if (hbcNodes && allHandledByCoas) {
            const updated = getUpdatedNodes(order.id, hbc, hbcNodes);

            if (updated) {
              data = {
                ...data,
                allHandledByCoas: {
                  ...allHandledByCoas,
                  nodes: updated,
                },
              };
            }
          }

          if (!isEqual(data, dataInStore)) {
            client.writeQuery({
              query: PlanningPageDocument,
              variables: planningPageQueryVariables,
              data,
            });
          }
        } catch (e) {
          console.error(e);
        }
      },
    };
  }, [organization_id]);
  usePlanningDataUpdatedSubscription(baseOptions);

  const organizationOptions: SubscriptionHookOptions<
    unknown,
    Exact<{ org: string }>
  > = useMemo(() => {
    return {
      variables: {
        org: String(organization_id),
      },
      skip: organization_id == null || !isBrowser,
      onData: function (
        options: OnDataOptions<OrganizationUpdatedSubscription>,
      ) {
        const { client, data } = options;
        const { organization, event } = data?.data?.organizationUpdated || {};
        const dataInStore = client.readQuery<PlanningPageQuery>({
          query: PlanningPageDocument,
          variables: planningPageQueryVariables,
        });
        const allOrganizations = dataInStore?.allOrganizations;
        const nodes = allOrganizations?.nodes;
        if (!nodes) {
          return;
        }
        if (organization) {
          const id = organization.id;
          if (!nodes.find((o) => o?.id === id)) {
            client.writeQuery({
              query: PlanningPageDocument,
              variables: planningPageQueryVariables,
              data: {
                ...dataInStore,
                allOrganizations: {
                  ...allOrganizations,
                  nodes: [...nodes, organization],
                },
              },
            });
          }
        } else if (event) {
          const id = +event;
          if (nodes.find((o) => o?.id === id)) {
            client.writeQuery({
              query: PlanningPageDocument,
              variables: planningPageQueryVariables,
              data: {
                ...dataInStore,
                allOrganizations: {
                  ...allOrganizations,
                  nodes: nodes.filter((o) => o?.id !== id),
                },
              },
            });
          }
        }

        /**
         * Update user organization descendants in global state This is
         * necessary because the user may have access to new organizations
         * created by external api that are not part of the current query
         */
        updateUserOrganizationDescendants(dataInStore);
      },
    };
  }, [organization_id]);
  useOrganizationUpdatedSubscription(organizationOptions);

  const shipOptions: SubscriptionHookOptions<
    unknown,
    Exact<{ org: string }>
  > = useMemo(() => {
    return {
      variables: {
        org: String(organization_id),
      },
      skip: organization_id == null || !isBrowser,
      onData: createPlanningPageQuerySubscriptionHandler(
        (data?: ShipsUpdatedSubscription) => {
          const { ship, event } = data?.shipUpdated || {};
          return {
            entity: ship,
            event,
          };
        },
        'allShips',
      ),
    };
  }, [organization_id]);
  useShipsUpdatedSubscription(shipOptions);

  const { data: allLocationsData } = useAllLocations();

  // Location not included in planning page query, but has its own
  const locationOptions: SubscriptionHookOptions<
    unknown,
    Exact<{ org: string }>
  > = useMemo(() => {
    return {
      variables: {
        org: String(organization_id),
      },
      skip: organization_id == null || !isBrowser,
      onData: function (
        options: OnDataOptions<LocationUpdatedSubscription>,
      ): void {
        const { data } = options;
        const { location, event } = data?.data?.locationUpdated || {};
        const nodes = allLocationsData?.allLocations?.nodes;

        if (!nodes) {
          return;
        }

        let updatedNodes = [...nodes];
        if (!location && event) {
          // Handle deletion
          const id = +event;
          updatedNodes = nodes.filter((o) => o?.id !== id);
        } else if (location) {
          // Handle addition/update
          const id = location.id;
          const existingIndex = nodes.findIndex((o) => o?.id === id);

          if (existingIndex === -1) {
            updatedNodes = [...nodes, location];
          } else {
            updatedNodes[existingIndex] = location;
          }
        }

        setAllLocationsResult({
          data: {
            ...allLocationsData,
            allLocations: {
              ...allLocationsData?.allLocations,
              nodes: updatedNodes,
            },
          },
          loading: false,
        });
      },
    };
  }, [allLocationsData, organization_id, setAllLocationsResult]);
  useLocationUpdatedSubscription(locationOptions);

  // All assortments not included in any top level query, so we just need to receive updates
  const assortmentOptions: SubscriptionHookOptions<
    unknown,
    Exact<{ org: string }>
  > = useMemo(() => {
    return {
      variables: {
        org: String(organization_id),
      },
      skip: organization_id == null || !isBrowser,
      onData: () => {},
    };
  }, [organization_id]);
  useAssortmentUpdatedSubscription(assortmentOptions);

  const voyageAttributeOptions: SubscriptionHookOptions<
    unknown,
    Exact<{ org: string }>
  > = useMemo(() => {
    return {
      variables: {
        org: String(organization_id),
      },
      skip: organization_id == null || !isBrowser,
      onData: (options: OnDataOptions<VoyageAttributeUpdatedSubscription>) => {
        const { client, data } = options;
        const { voyageAttribute, event } =
          data?.data?.voyageAttributeUpdated || {};
        const dataInStore = client.readQuery<AllVoyageAttributesQuery>({
          query: AllVoyageAttributesDocument,
        });
        const rootField = dataInStore?.allVoyageAttributes;
        const nodes = rootField?.nodes;
        if (!nodes) {
          return;
        }
        if (!voyageAttribute) {
          if (event) {
            const id = +event;
            if (nodes.find((o) => o?.id === id)) {
              client.writeQuery<AllVoyageAttributesQuery>({
                query: AllVoyageAttributesDocument,
                data: {
                  ...dataInStore,
                  allVoyageAttributes: {
                    ...rootField,
                    nodes: nodes.filter((o) => o?.id !== id),
                  },
                },
              });
            }
          }
          return;
        }
        const id = voyageAttribute.id;
        if (!nodes.find((o) => o?.id === id)) {
          client.writeQuery<AllVoyageAttributesQuery>({
            query: AllVoyageAttributesDocument,
            data: {
              ...dataInStore,
              allVoyageAttributes: {
                ...rootField,
                nodes: [...nodes, voyageAttribute],
              },
            },
          });
        }
      },
    };
  }, [organization_id]);
  useVoyageAttributeUpdatedSubscription(voyageAttributeOptions);

  const shipmentOptions: SubscriptionHookOptions<
    unknown,
    Exact<{ org: string }>
  > = useMemo(() => {
    return {
      variables: {
        org: String(organization_id),
      },
      skip: organization_id == null || !isBrowser,
      onData: function (
        options: OnDataOptions<ShipmentUpdatedSubscription>,
      ): void {
        const { client, data } = options;
        const { shipment, event } = data?.data?.shipmentUpdated || {};
        const dataInStore = client.readQuery<PlanningPageQuery>({
          query: PlanningPageDocument,
          variables: planningPageQueryVariables,
        });
        const allShipments = dataInStore?.allShipments;
        const nodes = allShipments?.nodes;
        if (!nodes) {
          return;
        }
        if (!shipment) {
          if (event) {
            const id = +event;
            if (nodes.find((o) => o?.id === id)) {
              client.writeQuery({
                query: PlanningPageDocument,
                variables: planningPageQueryVariables,
                data: {
                  ...dataInStore,
                  allShipments: {
                    ...allShipments,
                    nodes: nodes.filter((o) => o?.id !== id),
                  },
                },
              });
            }
          }
          return;
        }
        const id = shipment.id;
        if (!nodes.find((o) => o?.id === id)) {
          client.writeQuery({
            query: PlanningPageDocument,
            variables: planningPageQueryVariables,
            data: {
              ...dataInStore,
              allShipments: { ...allShipments, nodes: [...nodes, shipment] },
            },
          });
        }
      },
    };
  }, [organization_id]);
  useShipmentUpdatedSubscription(shipmentOptions);

  const robTaskOptions: SubscriptionHookOptions<
    unknown,
    Exact<{ org: string }>
  > = useMemo(() => {
    return {
      variables: {
        org: String(organization_id),
      },
      skip: organization_id == null || !isBrowser,
      onData: (options: OnDataOptions<RobTaskUpdateSubscription>) => {
        const { data, client } = options;
        const bunkerData = data.data?.robTaskUpdated?.bunker;
        if (bunkerData?.errorMessage) {
          toast(bunkerData.errorMessage, {
            type: 'error',
            autoClose: false,
          });
          return;
        }

        const dataInStore = client.readQuery<VesonBunkerQuery>({
          query: VesonBunkerDocument,
        });

        const cache = dataInStore?.getVesonBunker;
        if (cache && bunkerData) {
          const { defaultBunkerOfVessels, remainingBunkerOfVoyages } =
            bunkerData;

          const newDefaultBunker = [...(cache.defaultBunkerOfVessels ?? [])];
          if (defaultBunkerOfVessels?.length) {
            const defaultBunkerById = mapById(defaultBunkerOfVessels);
            // Filter outdated data
            for (let i = newDefaultBunker.length - 1; i >= 0; i--) {
              if (defaultBunkerById.has(newDefaultBunker[i].id)) {
                newDefaultBunker.splice(i, 1);
              }
            }

            // Add new data
            newDefaultBunker.push(...defaultBunkerOfVessels);
          }

          const newVoyageBunker = [...(cache.remainingBunkerOfVoyages ?? [])];
          if (remainingBunkerOfVoyages?.length) {
            const voyageBunkerById = mapById(remainingBunkerOfVoyages);
            // Filter oudated data
            for (let i = newVoyageBunker.length - 1; i >= 0; i--) {
              if (voyageBunkerById.has(newVoyageBunker[i].id)) {
                newVoyageBunker.splice(i, 1);
              }
            }
            // Add new data
            newVoyageBunker.push(...remainingBunkerOfVoyages);
          }

          const next = {
            ...dataInStore,
            getVesonBunker: {
              ...cache,
              defaultBunkerOfVessels: newDefaultBunker,
              remainingBunkerOfVoyagesvoyageRob: newVoyageBunker,
            },
          };
          client.writeQuery<VesonBunkerQuery>({
            query: VesonBunkerDocument,
            data: next,
          });
        }
        toast(`Update RoB completed`, {
          type: 'success',
          autoClose: 10000,
        });
      },
    };
  }, [organization_id]);
  useRobTaskUpdateSubscription(robTaskOptions);
}

/**
 * Updates the current user's organization descendants in global state
 */
function updateUserOrganizationDescendants(
  dataInStore: PlanningPageQuery | null,
) {
  // Early return if no organization data available
  if (!dataInStore?.allOrganizations?.nodes) return;

  const currentUser = globalStore.getState()?.currentUser;
  if (!currentUser?.userRights?.nodes) return;

  // Extract all available organization IDs fromg graphql data store
  const allOrgIds = dataInStore.allOrganizations.nodes
    .map((org) => org?.id)
    .filter(isDefined);

  // Get current organization descendants the user has access to
  const currentOrgDescendants = currentUser.userRights.nodes
    .filter(isDefined)
    .map((right) => right?.organizationByOrganizationId?.descendants)
    .flat()
    .filter(isDefined);

  // Find new organization IDs not in user's current descendants
  const newOrgIds = allOrgIds.filter(
    (id) => !currentOrgDescendants.includes(id),
  );

  if (newOrgIds.length === 0) return;

  const consolidatedOrgIds = [...currentOrgDescendants, ...newOrgIds];

  const updatedRights = currentUser.userRights.nodes
    .filter(isDefined)
    .map((node) => ({
      ...node,
      organizationByOrganizationId: node.organizationByOrganizationId
        ? {
            ...node.organizationByOrganizationId,
            descendants: consolidatedOrgIds,
          }
        : node.organizationByOrganizationId,
    }));

  globalStore.setState({
    currentUser: {
      ...currentUser,
      userRights: {
        ...currentUser.userRights,
        nodes: updatedRights,
      },
    },
  });
}

function getUpdatedNodes<T extends Record<string, unknown>>(
  orderId: number,
  incomingNode: T | null | undefined,
  currentNodes: T[],
) {
  let updatedNodes: T[] | null = null;
  const foundIndex = currentNodes.findIndex((node) => node.orderId === orderId);

  if (!incomingNode && foundIndex > -1) {
    // Delete existing node as it was deleted in db
    updatedNodes = currentNodes;
    updatedNodes.splice(foundIndex, 1);
  }

  if (incomingNode && !isEqual(incomingNode, currentNodes[foundIndex])) {
    if (foundIndex > -1) {
      // Existing node was updated in db
      updatedNodes = currentNodes;
      updatedNodes[foundIndex] = incomingNode;
    } else {
      // Append new node
      updatedNodes = [...currentNodes, incomingNode];
    }
  }

  return updatedNodes;
}
